import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { onSnapshot, orderBy, where, Timestamp } from "firebase/firestore";
import { orgQuery } from "@/lib/org";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { useStudents } from "@/hooks/useStudents";
import { fmtDateTime, fmtYearMonth } from "@/lib/format";
import { BRAND_COLORS } from "@/lib/brands";
import MapView, { Marker, Popup } from "@/components/MapView";
import { dotIcon, STATUS_COLORS } from "@/lib/map";
import { Delivery, DriverProfile } from "@/types";
import { kstDay } from "@/lib/kst";
import { downloadXlsx } from "@/lib/exportXlsx";
import CancelDelivery from "@/components/CancelDelivery";
import CanceledDeliveries from "@/components/CanceledDeliveries";

import { plainError } from "@/lib/plainError";
/**
 * 실시간 배송 관제 센터.
 * - 상단 KPI
 * - 좌측: 실시간 지도 (완료/대기/택배)
 * - 우측: 최근 배송 피드 (사진 썸네일)
 * - 하단: 기사별 진행률
 * - 예외 경보: 지도 위치 없음/주소 이상/사진 없음
 */
// 최근 N개월의 "YYYY-MM" 목록 (현재월 → 과거순)
function recentMonths(n = 12): string[] {
  const now = new Date();
  return Array.from({ length: n }, (_, i) => fmtYearMonth(new Date(now.getFullYear(), now.getMonth() - i, 1)));
}

export default function MissionControl() {
  const { students } = useStudents();
  // 조회 월 — 기본은 현재월. 과거 월을 고르면 그 달 배송기록·사진을 조회한다.
  const [ym, setYm] = useState(fmtYearMonth());
  const monthOptions = useMemo(() => recentMonths(12), []);
  const isCurrentMonth = ym === fmtYearMonth();
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [drivers, setDrivers] = useState<DriverProfile[]>([]);
  const [feedQuery, setFeedQuery] = useState("");
  // 피드에서 보호자에게 추가 문자 보내기
  const [smsFor, setSmsFor] = useState<string | null>(null); // deliveryId
  const [smsText, setSmsText] = useState("");
  const [smsStatus, setSmsStatus] = useState<Record<string, string>>({});
  // 배송완료 취소 — 펼친 기록과 마지막 취소 결과(취소된 기록은 목록에서 바로 빠지므로 결과는 피드 위에 남긴다)
  const [cancelFor, setCancelFor] = useState<string | null>(null);
  const [cancelResult, setCancelResult] = useState("");

  const sendCustomSms = async (d: any) => {
    const text = smsText.trim();
    if (!text) return;
    setSmsStatus((s) => ({ ...s, [d.id]: "발송 중…" }));
    try {
      const fn = httpsCallable<{ studentId: string; text: string }, { ok: boolean; reason?: string }>(functions, "sendCustomSms");
      const res = await fn({ studentId: d.studentId, text });
      const r = res.data;
      setSmsStatus((s) => ({ ...s, [d.id]: r.ok ? "✅ 문자 발송됨" : `⚠️ 실패: ${r.reason}` }));
      if (r.ok) { setSmsFor(null); setSmsText(""); }
    } catch (e: any) {
      setSmsStatus((s) => ({ ...s, [d.id]: `⚠️ ${plainError(e, "발송 실패")}` }));
    }
  };

  useEffect(() => {
    const q = orgQuery(
      "deliveries",
      where("yearMonth", "==", ym),
      orderBy("deliveredAt", "desc"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setDeliveries(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
      },
      (err) => console.error("배송 관제 deliveries 쿼리 실패(인덱스 확인 필요)", err),
    );
    return () => unsub();
  }, [ym]);

  useEffect(() => {
    const unsub = onSnapshot(orgQuery("drivers"), (snap) => {
      setDrivers(snap.docs.map((d) => ({ uid: d.id, ...(d.data() as any) })));
    });
    return () => unsub();
  }, []);

  const deliveredIds = useMemo(() => new Set(deliveries.map((d) => d.studentId)), [deliveries]);

  // 배송 전체 대상 (택배건 제외)
  const allTargets = useMemo(() => students.filter((s) => !s.courierFlag), [students]);
  const couriered = students.filter((s) => s.courierFlag);

  const todayStr = kstDay();
  const todayCount = deliveries.filter((d) => {
    const t = (d.deliveredAt as any)?.toDate?.() ?? ((d.deliveredAt as any)?.seconds ? new Date((d.deliveredAt as any).seconds * 1000) : null);
    return t && kstDay(t) === todayStr;
  }).length;
  // 완료 수는 '배송기록 건수'가 아니라 '배송된 학생 수'로 센다.
  // 기사가 완료 버튼을 두 번 눌러 같은 학생에게 기록이 2건 쌓이면 건수 기준은 완료가 부풀려져
  // 미완료가 실제보다 적게 나오고, 택배 리스트의 미배송 수와 어긋난다.
  const monthDone = allTargets.filter((s) => deliveredIds.has(s.id)).length;
  const monthTarget = allTargets.length;
  const monthProgress = monthTarget ? (monthDone / monthTarget) * 100 : 0;

  // 예외 경보
  const noCoords = allTargets.filter((s) => !s.lat || !s.lng);
  const noPhone = allTargets.filter((s) => !s.phone);
  // 배송 경로에서 누락되는 학생: 지도 위치 없음 또는 동 이름 없음(담당 매칭 불가)
  const atRisk = allTargets.filter((s) => !s.lat || !s.lng || !s.dong);
  const pendingCount = monthTarget - monthDone;

  // 기사 실적 (이번달)
  const driverStats = drivers.map((d) => ({
    ...d,
    monthCount: d.stats?.[`m_${ym}`] || 0,
  })).sort((a, b) => b.monthCount - a.monthCount);

  // 배송 기록 내려받기 — 교육청 실적 제출·해지 전 보관용(예행연습 03-12)
  const exportDeliveries = () => {
    const rows = deliveries.map((d: any) => ({
      배송일시: d.deliveredAt?.toDate ? fmtDateTime(d.deliveredAt.toDate()) : "", 학교: d.school || "", 이름: d.studentName || "",
      기사: d.driverName || "", 브랜드: d.brand || "", 수량: d.boxCount ?? 1,
      보호자알림: d.notifyOk === true ? "보냄" : d.notifyOk === false ? `실패(${d.notifyReason || ""})` : "",
      사진링크: d.photoUrl || "", 메모: d.note || "",
    }));
    downloadXlsx(`배송기록_${ym}_${rows.length}건.xlsx`, `${ym} 배송`, rows);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-bold">🎛️ 배송 관제 센터</h1>
          <select
            className="input max-w-[130px] text-sm"
            value={ym}
            onChange={(e) => setYm(e.target.value)}
          >
            {monthOptions.map((m) => (
              <option key={m} value={m}>{m}{m === fmtYearMonth() ? " (이번달)" : ""}</option>
            ))}
          </select>
          {!isCurrentMonth && <span className="chip bg-amber-100 text-amber-800 text-xs">과거 기록 조회</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button className="btn-secondary text-xs" onClick={exportDeliveries} disabled={!deliveries.length} title="이 달 배송 기록(누가 언제 받았는지·사진 링크)을 엑셀로 받습니다">
            📄 {ym} 배송 기록 내려받기 ({deliveries.length}건)
          </button>
          <span className="text-xs text-slate-500">{isCurrentMonth ? "실시간" : `${ym} 배송 기록·사진`}</span>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Kpi title="이번달 대상" value={monthTarget} />
        <Kpi title="이번달 완료" value={monthDone} tone="success" />
        <Kpi title="이번달 미완료" value={pendingCount} tone="warning" />
        <Kpi title="오늘 배송" value={todayCount} />
        <Kpi title="진행률" value={`${monthProgress.toFixed(1)}%`} tone="info" />
      </div>

      {/* 진행률 바 */}
      <div className="card p-3">
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>{ym} 전체 진행률</span>
          <span>{monthDone} / {monthTarget}</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-milk-400 to-emerald-500" style={{ width: `${monthProgress}%` }} />
        </div>
      </div>

      {/* 배송 누락 위험 경보 — 지도 위치/동 이름 미상은 경로·지도에서 빠짐 */}
      {atRisk.length > 0 && (
        <div className="card p-3 border border-rose-200 bg-rose-50">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-bold text-rose-700">⚠️ 배송 누락 위험 {atRisk.length}명 (지도 위치·동 이름 미상)</h3>
            <Link to="/admin/address-confirm" className="btn-secondary text-xs">주소 확인 대기로 이동 →</Link>
          </div>
          <p className="text-xs text-rose-600 mt-1">
            지도 위치나 동 이름이 없어 지도·배송 경로에 안 나옵니다. 주소를 확인하고 「지도 위치 찾기」를 눌러 주세요.
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {atRisk.map((s) => (
              <span key={s.id} className="chip bg-white border border-rose-200 text-rose-700 text-2xs">
                {s.school} {s.name}{!s.lat || !s.lng ? " · 지도 위치X" : ""}{!s.dong ? " · 동X" : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* 지도 */}
        <div className="lg:col-span-2 space-y-2">
          <div className="card p-2 text-xs text-slate-500 flex items-center gap-4 flex-wrap">
            <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full" style={{ background: STATUS_COLORS.done }} /> 완료</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full" style={{ background: STATUS_COLORS.pending }} /> 미배송</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full" style={{ background: STATUS_COLORS.courier }} /> 월말 택배</span>
            <span className="ml-auto opacity-70">마우스 휠 줌</span>
          </div>
          <MapView height="600px">
            {allTargets
              .filter((s) => s.lat && s.lng)
              .map((s) => {
                const done = deliveredIds.has(s.id);
                return (
                  <Marker
                    key={s.id}
                    position={[s.lat!, s.lng!]}
                    icon={dotIcon(done ? STATUS_COLORS.done : STATUS_COLORS.pending, 9)}
                  >
                    <Popup>
                      <div className="text-xs">
                        <div className="font-bold">{s.school} {s.name}</div>
                        <div>{s.refinedAddress}</div>
                        <div className="mt-1 text-slate-500">{done ? "✅ 이번달 배송 완료" : "⏳ 미배송"}</div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            {couriered
              .filter((s) => s.lat && s.lng)
              .map((s) => (
                <Marker
                  key={`c-${s.id}`}
                  position={[s.lat!, s.lng!]}
                  icon={dotIcon(STATUS_COLORS.courier, 9)}
                >
                  <Popup>
                    <div className="text-xs">
                      <div className="font-bold">{s.school} {s.name}</div>
                      <div>{s.refinedAddress}</div>
                      <div className="mt-1 text-amber-700">📮 월말 택배</div>
                    </div>
                  </Popup>
                </Marker>
              ))}
          </MapView>
        </div>

        {/* 실시간 피드 */}
        <div className="card p-3 flex flex-col max-h-[660px]">
          <h3 className="font-bold mb-1 sticky top-0 bg-white">📡 실시간 배송 피드 ({deliveries.length}건)</h3>
          <input
            className="input text-sm mb-2"
            placeholder="학생/학교/기사 검색…"
            value={feedQuery}
            onChange={(e) => setFeedQuery(e.target.value)}
          />
          {cancelResult && (
            <div className="mb-2 rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-xs text-emerald-900" role="status">
              {cancelResult}
              <button className="ml-2 underline" onClick={() => setCancelResult("")}>닫기</button>
            </div>
          )}
          <div className="flex-1 overflow-auto space-y-2">
            {(() => {
              const q = feedQuery.trim().toLowerCase();
              const list = q
                ? deliveries.filter((d: any) =>
                    [d.studentName, d.school, d.driverName].some((v) => (v || "").toLowerCase().includes(q)),
                  )
                : deliveries.slice(0, 40);
              return list.map((d: any) => (
              <div key={d.id} className="p-2 rounded-lg hover:bg-slate-50 border border-slate-100">
                <div className="flex gap-3">
                  {d.photoUrl ? (
                    <a href={d.photoUrl} target="_blank" rel="noreferrer" className="shrink-0">
                      <img src={d.photoUrl} className="w-14 h-14 object-cover rounded" />
                    </a>
                  ) : (
                    <div className="w-14 h-14 bg-slate-100 rounded flex items-center justify-center text-xs text-slate-400">사진</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{d.school} {d.studentName}</div>
                    <div className="text-xs text-slate-500 truncate">기사: {d.driverName}</div>
                    <div className="text-xs text-slate-400">
                      {d.deliveredAt ? fmtDateTime(tsToDate(d.deliveredAt)) : "—"}
                    </div>
                    <button
                      className="text-2xs text-milk-600 hover:text-milk-700 mt-0.5"
                      onClick={() => { setSmsFor(smsFor === d.id ? null : d.id); setSmsText(""); }}
                    >
                      📩 부모님께 문자
                    </button>
                    <button
                      className="text-2xs text-rose-600 hover:text-rose-700 mt-0.5 ml-3"
                      onClick={() => setCancelFor(cancelFor === d.id ? null : d.id)}
                    >
                      ↩️ 완료 취소
                    </button>
                  </div>
                  <span className={`chip text-[10px] ${BRAND_COLORS[d.brand as keyof typeof BRAND_COLORS] ?? "bg-slate-100"}`}>{d.brand}</span>
                </div>
                {smsFor === d.id && (
                  <div className="mt-2 space-y-1">
                    <textarea
                      className="input text-xs"
                      rows={2}
                      placeholder={`${d.studentName} 학부모께 보낼 메시지 (자동으로 [무상우유] 머리말 추가)`}
                      value={smsText}
                      onChange={(e) => setSmsText(e.target.value)}
                    />
                    <div className="flex gap-1">
                      <button className="btn-primary btn-sm" onClick={() => sendCustomSms(d)}>보내기</button>
                      <button className="btn-ghost btn-sm" onClick={() => setSmsFor(null)}>취소</button>
                    </div>
                  </div>
                )}
                {smsStatus[d.id] && <div className="text-2xs text-slate-600 mt-1">{smsStatus[d.id]}</div>}
                {cancelFor === d.id && (
                  <div className="mt-2">
                    {/* 잘못 찍은 배송 되돌리기(4차 점검 17) — 취소되면 실시간 목록에서 빠지고 결과는 위 칸에 남는다 */}
                    <CancelDelivery delivery={d} yearMonth={ym} onCanceled={(m) => setCancelResult(m)} />
                  </div>
                )}
              </div>
              ));
            })()}
            {deliveries.length === 0 && (
              <div className="text-center text-sm text-slate-400 py-10">아직 배송된 기록이 없습니다</div>
            )}
            {deliveries.length > 0 && feedQuery.trim() === "" && deliveries.length > 40 && (
              <div className="text-center text-2xs text-slate-400 py-2">최근 40건 표시 · 나머지는 검색</div>
            )}
          </div>
        </div>
      </div>

      {/* 그달 완료 취소된 배송 — 누가·언제·정정 문자 결과 */}
      <CanceledDeliveries yearMonth={ym} />

      {/* 기사별 진행률 */}
      <section className="card p-4">
        <h3 className="font-bold mb-3">🚚 기사별 실적 ({ym})</h3>
        <div className="space-y-2">
          {driverStats.length === 0 && <div className="text-sm text-slate-400 text-center py-4">등록된 기사가 없습니다</div>}
          {driverStats.map((d) => {
            const maxVal = Math.max(1, ...driverStats.map((x) => x.monthCount));
            const pct = (d.monthCount / maxVal) * 100;
            return (
              <div key={d.uid} className="flex items-center gap-3 text-sm">
                <div className="w-40 truncate">{d.displayName || d.uid.slice(0, 8)}</div>
                <span className={`chip text-[10px] ${d.role === "admin" ? "bg-rose-100 text-rose-700" : "bg-milk-100 text-milk-700"}`}>{d.role}</span>
                <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden">
                  <div className="h-full bg-milk-500" style={{ width: `${pct}%` }} />
                </div>
                <div className="w-14 text-right font-bold">{d.monthCount}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 예외 경보 */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Alert title="지도 위치 없음" count={noCoords.length} desc="지도 위치 찾기 필요" tone="warning" />
        <Alert title="연락처 없음" count={noPhone.length} desc="SMS 발송 불가 건" tone="warning" />
        <Alert title="월말 택배 전환" count={couriered.length} desc="택배 파일 받기 필요" tone="info" />
      </section>
    </div>
  );
}

function Kpi({ title, value, tone }: { title: string; value: number | string; tone?: "success" | "warning" | "info" }) {
  const color =
    tone === "success" ? "text-emerald-600" :
    tone === "warning" ? "text-amber-600" :
    tone === "info" ? "text-milk-700" : "text-slate-800";
  return (
    <div className="card p-3">
      <div className="label">{title}</div>
      <div className={`text-2xl font-extrabold mt-1 ${color}`}>{typeof value === "number" ? value.toLocaleString() : value}</div>
    </div>
  );
}

function Alert({ title, count, desc, tone }: { title: string; count: number; desc: string; tone: "warning" | "info" }) {
  const color = tone === "warning" ? "border-amber-300 bg-amber-50" : "border-milk-300 bg-milk-50";
  return (
    <div className={`card p-3 ${color}`}>
      <div className="flex items-center justify-between">
        <div className="font-bold">{title}</div>
        <div className="text-xl font-extrabold">{count}</div>
      </div>
      <div className="text-xs text-slate-500 mt-1">{desc}</div>
    </div>
  );
}

function tsToDate(ts: any): Date {
  if (ts?.toDate) return ts.toDate();
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
}
